import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2, Loader2, AlertTriangle, Download, CheckCircle2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";

interface DeleteAccountDialogProps {
  onConfirm: () => Promise<void>;
  onExportData: () => Promise<void>;
  isDeleting: boolean;
}

export function DeleteAccountDialog({ onConfirm, onExportData, isDeleting }: DeleteAccountDialogProps) {
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [hasExported, setHasExported] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const isConfirmValid = confirmText === "DELETE";

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await onExportData();
      setHasExported(true);
    } finally {
      setIsExporting(false);
    }
  };

  const handleConfirm = async () => {
    if (!isConfirmValid) return;
    await onConfirm();
    setOpen(false);
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!isDeleting) {
      setOpen(newOpen);
      if (!newOpen) {
        // Reset state when closing
        setConfirmText("");
        setHasExported(false);
      }
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogTrigger asChild>
        <Button variant="destructive" className="w-full sm:w-auto">
          <Trash2 className="mr-2 h-4 w-4" />
          Account löschen
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="max-w-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Account dauerhaft löschen
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-4 text-left">
            <Alert variant="destructive" className="mt-4">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <strong>Diese Aktion kann nicht rückgängig gemacht werden!</strong>
                <br />
                Alle deine Daten werden dauerhaft gelöscht, einschließlich:
              </AlertDescription>
            </Alert>

            <ul className="list-disc list-inside space-y-1 text-sm ml-4">
              <li>Dein Profil und persönliche Informationen</li>
              <li>Alle Entscheidungs-Sessions und Analysen</li>
              <li>Team-Mitgliedschaften und Berechtigungen</li>
              <li>Hochgeladene Dateien und Dokumente</li>
              <li>Benachrichtigungen und Einstellungen</li>
            </ul>

            <div className="bg-muted p-4 rounded-lg space-y-3 mt-4">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label className="text-base font-medium">
                    Daten exportieren (optional)
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Lade eine Kopie deiner Daten herunter, bevor du deinen Account löschst
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExport}
                  disabled={isExporting || hasExported}
                >
                  {isExporting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Exportiere...
                    </>
                  ) : hasExported ? (
                    <>
                      <CheckCircle2 className="mr-2 h-4 w-4" />
                      Exportiert
                    </>
                  ) : (
                    <>
                      <Download className="mr-2 h-4 w-4" />
                      Export
                    </>
                  )}
                </Button>
              </div>
              
              {hasExported && (
                <div className="flex items-center gap-2 text-xs text-green-600 dark:text-green-400">
                  <Checkbox checked disabled />
                  <span>Daten wurden erfolgreich exportiert</span>
                </div>
              )}
            </div>

            <div className="space-y-2 mt-6">
              <Label htmlFor="confirm-delete">
                Gib <strong>DELETE</strong> ein, um zu bestätigen
              </Label>
              <Input
                id="confirm-delete"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE"
                className="font-mono"
                disabled={isDeleting}
              />
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Abbrechen</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={!isConfirmValid || isDeleting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isDeleting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Lösche Account...
              </>
            ) : (
              <>
                <Trash2 className="mr-2 h-4 w-4" />
                Account unwiderruflich löschen
              </>
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
